import React from "react";
import { Models } from "@hex-insights/app-modules";
import {
	Button,
	Column,
	Conditional,
	Else,
	englishCommaJoinStrings,
	FormattedText,
	Heading,
	If,
	makeClassName,
	Modal,
	ModalProps,
	NotificationContainer,
	NotificationRenderProps,
	Paragraph,
	RequiredKeys,
	Row,
	Tooltip,
	TooltipButton,
	TruncatedText,
} from "@hex-insights/core";
import { EditMode } from "@hex-insights/forms";
import { Icon } from "../../../../Components/Icon";
import { applicationDetails } from "../../../../settings";
import {
	Message,
	MessageGroupMessagesListQuery,
	useMessageDetailForEditQuery,
	UserFilterInput,
	UserViewRecordListForTargetWithUserQuery,
} from "../../../../Utilities";
import { UserFormat } from "../../../Format";
import { MessageCustomForm } from "../../../Forms";
import { MessageMutation } from "../../../FormUtilities";
import { MediaAttachmentUtilities } from "../../../Utilities";
import * as MediaAttachmentComponents from "../../MediaAttachment";
import { ControlledUsersListModal } from "../../User";
import { useMessageDeletePermissionCheck, useMessageUpdatePermissionCheck } from "../PermissionRequired";
import styles from "./styles.module.css";

export type MessageViewProps = {
	message: MessageGroupMessagesListQuery["messageConnection"]["edges"][0]["node"];
	viewRecords?: UserViewRecordListForTargetWithUserQuery["userViewRecordConnection"]["edges"][0]["node"][];
	isInOneOnOne?: boolean;
};

export function MessageView({ message, viewRecords, isInOneOnOne = false }: MessageViewProps) {
	const { visualAttachments, fileAttachments, linkAttachments } = React.useMemo(
		() => MediaAttachmentUtilities.splitAndExtractMediaAttachments(message.mediaAttachments, message.body),
		[message.mediaAttachments, message.body],
	);

	const { isMediaAttachmentModalOpen, onClickMediaAttachment, closeMediaAttachmentModal, mediaAttachmentModalIndex } =
		MediaAttachmentComponents.MediaAttachmentModal.useModal();

	const isBackgroundBlue = false;
	const showViewRecords = !!viewRecords;

	const [baseShowMutationMenuButton, setShowMutationMenuButton] = React.useState(false);
	const onMouseEnter = React.useCallback(() => {
		setShowMutationMenuButton(true);
	}, []);
	const onMouseLeave = React.useCallback(() => {
		setShowMutationMenuButton(false);
	}, []);
	const [isMutationTooltipOpen, setIsMutationTooltipOpen] = React.useState(false);
	const [isMutationModalOpen, setIsMutationModalOpen] = React.useState(false);
	React.useEffect(() => {
		if (!isMutationModalOpen) {
			setShowMutationMenuButton(false);
		}
	}, [isMutationModalOpen]);

	const hasUpdatePermission = useMessageUpdatePermissionCheck(message.author.isCurrent, message.createdAt);
	const hasDeletePermission = useMessageDeletePermissionCheck(message.author.isCurrent);

	const showMutationMenuButton =
		(baseShowMutationMenuButton || isMutationTooltipOpen || isMutationModalOpen) &&
		(hasUpdatePermission || hasDeletePermission);

	if (message.isRemoved) {
		return <div className={styles["message"] + " " + styles["message--removed"]}>Message Removed</div>;
	}

	return (
		<div onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
			<Column
				justify="spaced-start"
				verticalSpacing="0.5rem"
				className={makeClassName(
					styles["message"],
					isBackgroundBlue ? styles["message--over-blue"] : "",
					showMutationMenuButton ? styles["message--hover"] : "",
				)}
			>
				<FormattedText>{message.body}</FormattedText>

				<If condition={visualAttachments.length > 0}>
					<MediaAttachmentComponents.VisualAttachmentsRow
						attachments={visualAttachments}
						onClickAttachment={onClickMediaAttachment}
					/>

					<Modal.If condition={isMediaAttachmentModalOpen}>
						<MediaAttachmentComponents.MediaAttachmentModal
							mediaAttachments={visualAttachments}
							initialIndex={mediaAttachmentModalIndex}
							onClose={closeMediaAttachmentModal}
						/>
					</Modal.If>
				</If>
				<If condition={fileAttachments.length > 0}>
					<MediaAttachmentComponents.FileAttachmentsRow
						attachments={fileAttachments}
						attachmentClassName={styles["message__file-attachment"]}
					/>
				</If>
				<If condition={linkAttachments.length > 0}>
					<MediaAttachmentComponents.LinkAttachmentsRow
						attachments={linkAttachments}
						attachmentClassName={styles["message__link-attachment"]}
					/>
				</If>

				{message.lastEditedAt && (
					<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem" className={styles["message__edited"]}>
						<Icon.PencilSimple size={12} /> Edited
					</Row>
				)}

				<If condition={showMutationMenuButton}>
					<div className={styles["mutation-menu-button-container"]}>
						{/* TODO side handling: top can get covered by the MessageGroup header, bottom gets covered by next message */}
						<TooltipButton side="left" onOpenChange={setIsMutationTooltipOpen}>
							{() => (
								<Tooltip isInteractable>
									<Tooltip.Body>
										<Column justify="spaced-start" verticalSpacing="0.25rem">
											{hasUpdatePermission && (
												<EditController message={message} setIsMutationModalOpen={setIsMutationModalOpen} />
											)}
											{hasDeletePermission && <DeleteButton message={message} />}
										</Column>
									</Tooltip.Body>
								</Tooltip>
							)}
							{({ onClick }) => (
								<Button variant="link" size="small" onClick={onClick} className="button--icon">
									<Icon.DotsThreeVertical size={16} className="block" />
								</Button>
							)}
						</TooltipButton>
					</div>
				</If>

				{!!showViewRecords && (
					<If condition={viewRecords.length > 0}>
						<ViewReceipt viewRecords={viewRecords} isInOneOnOne={isInOneOnOne} />
					</If>
				)}
			</Column>
		</div>
	);
}

type EditControllerProps = {
	message: MessageGroupMessagesListQuery["messageConnection"]["edges"][0]["node"];
	setIsMutationModalOpen: (v: boolean) => void;
};

function EditController({ message, setIsMutationModalOpen }: EditControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	React.useEffect(() => {
		setIsMutationModalOpen(isModalOpen);
	}, [setIsMutationModalOpen, isModalOpen]);

	return (
		<React.Fragment>
			<Button variant="link" size="small" onClick={openModal}>
				<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
					<Icon.PencilSimple size={14} /> Edit
				</Row>
			</Button>

			<Modal.If condition={isModalOpen}>
				<EditModal messageID={message.id} onClose={closeModal} />
			</Modal.If>
		</React.Fragment>
	);
}

const editFormStateArgs = { editMode: EditMode.WriteOnly };

type EditModalProps = {
	messageID: Message["id"];
} & RequiredKeys<Pick<ModalProps, "ifRef" | "onClose">, "onClose">;

function EditModal({ messageID, ifRef, onClose }: EditModalProps) {
	const { data } = useMessageDetailForEditQuery({ variables: { id: messageID } });
	const message = data?.message;

	return (
		<Modal ifRef={ifRef} onClose={onClose} className={styles["edit-modal"]}>
			<Modal.Header>
				<Heading.H2 noMargin>Edit Message</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<Conditional>
					<If condition={!!message}>
						{!!message && (
							<MessageCustomForm.ControlledEdit
								message={message}
								formStateArgs={editFormStateArgs}
								onSuccess={onClose}
							/>
						)}
					</If>
					<Else>Loading...</Else>
				</Conditional>
			</Modal.Body>
		</Modal>
	);
}

type DeleteButtonProps = {
	message: MessageGroupMessagesListQuery["messageConnection"]["edges"][0]["node"];
};

function DeleteButton({ message }: DeleteButtonProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const update = MessageMutation.useDirectUpdate<"isRemoved">(message.id);
	const applyUpdate = React.useCallback(async () => {
		return update({ isRemoved: true }, { isRemoved: false });
	}, [update]);

	return (
		<React.Fragment>
			<Button size="small" onClick={openModal} className="button--danger-link">
				<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
					<Icon.TrashSimple size={14} /> Delete
				</Row>
			</Button>

			<Modal.If condition={isModalOpen}>
				<Modal onClose={closeModal} style={{ width: "25rem" }}>
					<Modal.Header>
						<Heading.H2 noMargin>Delete Message</Heading.H2>
					</Modal.Header>
					<Modal.Body>
						<Paragraph>Are you sure you want to delete this message?</Paragraph>
						<Row justify="spaced-end">
							<Button variant="secondary" onClick={closeModal}>
								Cancel
							</Button>

							<Models.ActionButton
								variant="danger"
								performAction={applyUpdate}
								onSuccess={closeModal}
								SuccessNotification={DeleteSuccessNotification}
							>
								Delete Message
							</Models.ActionButton>
						</Row>
					</Modal.Body>
				</Modal>
			</Modal.If>
		</React.Fragment>
	);
}

function DeleteSuccessNotification(props: NotificationRenderProps) {
	return (
		<NotificationContainer variant="success" {...props}>
			Message deleted.
		</NotificationContainer>
	);
}

type ViewReceiptProps = {
	viewRecords: UserViewRecordListForTargetWithUserQuery["userViewRecordConnection"]["edges"][0]["node"][];
	isInOneOnOne: boolean;
};

function ViewReceipt({ viewRecords, isInOneOnOne }: ViewReceiptProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const areNamesTruncated = viewRecords.length > 2;
	const nameDisplay = areNamesTruncated
		? `${UserFormat.name(viewRecords[0].user)} and ${viewRecords.length - 1} others`
		: englishCommaJoinStrings(viewRecords.map((e) => UserFormat.name(e.user)));

	const userFilters = React.useMemo<UserFilterInput>(
		() => ({
			idIn: viewRecords.map((e) => e.user.id),
		}),
		[viewRecords],
	);

	return (
		<React.Fragment>
			<Row
				justify="spaced-start"
				align="center"
				horizontalSpacing="0.25rem"
				overflow="wrap"
				className={styles["message__view-records"]}
			>
				<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem" style={{ minWidth: 0 }}>
					<Icon.Check size={12} />
					<TruncatedText>
						Seen <If condition={!isInOneOnOne}>by {nameDisplay}</If>
					</TruncatedText>
				</Row>
				<If condition={areNamesTruncated}>
					<Button
						variant="link"
						size="small"
						onClick={openModal}
						className={styles["message__view-records__view-all-button"] + " nowrap"}
					>
						View All
					</Button>
				</If>
			</Row>

			<Modal.If condition={isModalOpen}>
				<ControlledUsersListModal heading="Seen By" filters={userFilters} onClose={closeModal} />
			</Modal.If>
		</React.Fragment>
	);
}
